import React, { useId, useRef, useState } from "react";
import { Moment } from "moment";
import Papa from "papaparse";
import { useIntl } from "react-intl";
import UIService from "../../../services/UIService";
import IBackfillCartCommissionsDTO, { IBackfillCartCommissionsResult } from "../../../models/DTO/IBackfillCartCommissionsDTO";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import CartCommissionService from "../../../services/CartCommissionService";
import OwButton from "../../shared/OwButton/OwButton";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";
import WhatsThis from "../../shared/WhatsThis/WhatsThis";

export const DEFAULT_BATCH_SIZE = 200;
export const MAX_BATCH_SIZE = 1000;

// Values are translation keys.
const totalLabels = {
    totalProcessed: "processed",
    totalUpdated: "updated",
    totalSkipped: "skipped",
    totalUnresolved: "unresolved",
    totalErrors: "errors",
};
const totalKeys = Object.keys(totalLabels) as (keyof typeof totalLabels)[];
const emptyResult = (): IBackfillCartCommissionsResult => ({
    totalProcessed: 0,
    totalUpdated: 0,
    totalSkipped: 0,
    totalUnresolved: 0,
    totalErrors: 0,
    unresolvedDetails: [],
    errorDetails: [],
    nextAfterId: null,
});
const hasIssues = (result: IBackfillCartCommissionsResult) =>
    result.totalErrors > 0 || result.totalUnresolved > 0 || result.errorDetails.length > 0;

type Mode = "run" | "continue" | "retry";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    notifyFn: (data: IErrorDTO | null, notification?: INotification, isFormatted?: boolean) => void;
    onLockedChange?: (running: boolean) => void;
    /** Portal for the calendar popper so it is not clipped by the modal's scroll container. */
    portalId?: string;
};

export default function CartCommissionBackfillPanel({ userModel, supplierModel, notifyFn, onLockedChange, portalId }: Props) {
    const intl = useIntl();
    const batchSizeId = useId();
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [batchSize, setBatchSize] = useState<string>(String(DEFAULT_BATCH_SIZE));
    const [running, setRunning] = useState(false);
    const [requestError, setRequestError] = useState("");
    // Translation key of the current validation message.
    const [validationError, setValidationError] = useState("");
    // Cumulative totals for this run (all batches so far).
    const [result, setResult] = useState<IBackfillCartCommissionsResult | null>(null);
    const [lastBatch, setLastBatch] = useState<IBackfillCartCommissionsResult | null>(null);
    const [done, setDone] = useState(false);
    // Synchronous guard also prevents clicks before React commits the disabled state.
    const active = useRef(false);
    const pending = useRef<IBackfillCartCommissionsDTO | null>(null);
    const totals = useRef(emptyResult());

    const process = async (mode: Mode) => {
        if (active.current) return;
        if (mode === "run") {
            if (!startDate || !startDate.isValid()) {
                setValidationError("cart_backfill_start_date_required");
                return;
            }
            const limit = batchSize.trim() === "" ? NaN : Number(batchSize);
            if (!Number.isInteger(limit) || limit < 1 || limit > MAX_BATCH_SIZE) {
                setValidationError("cart_backfill_batch_size_invalid");
                return;
            }
            pending.current = { startDate: startDate.format("YYYY-MM-DD"), limit, afterId: 0 };
            totals.current = emptyResult();
            setResult(totals.current);
            setLastBatch(null);
            setDone(false);
        } else if (mode === "continue") {
            if (!pending.current || totals.current.nextAfterId === null) return;
            pending.current = { ...pending.current, afterId: totals.current.nextAfterId };
        }
        // "retry" re-sends pending.current unchanged.
        if (!pending.current) return;
        active.current = true;
        setRunning(true);
        onLockedChange?.(true);
        setValidationError("");
        setRequestError("");
        try {
            const batch = await CartCommissionService.backfillCommissions(pending.current, userModel, supplierModel);
            const next = { ...totals.current, nextAfterId: batch.nextAfterId };
            totalKeys.forEach((key) => {
                next[key] += batch[key];
            });
            next.unresolvedDetails = [...next.unresolvedDetails, ...batch.unresolvedDetails];
            next.errorDetails = [...next.errorDetails, ...batch.errorDetails];
            totals.current = next;
            setResult(next);
            setLastBatch(batch);
            const finished = batch.nextAfterId === null;
            setDone(finished);
            if (finished) {
                pending.current = null;
            }
            const issues = hasIssues(batch);
            let message: string;
            if (finished) {
                message = issues ? "cart_backfill_completed_with_issues" : "cart_backfill_completed";
            } else {
                message = issues ? "cart_backfill_batch_completed_with_issues" : "cart_backfill_batch_completed";
            }
            notifyFn(null, {
                level: issues ? NotificationLevelEnum.warning : NotificationLevelEnum.success,
                position: NotificationPositionEnum.tr,
                message,
            });
        } catch (error) {
            // Server/fetch messages are dynamic text, not translation keys; render them verbatim.
            const message = error instanceof Error && error.message ? error.message : intl.formatMessage({ id: "cart_backfill_failed" });
            setRequestError(message);
            notifyFn(null, {
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.tr,
                message,
                skipMessageTranslate: true,
            });
        } finally {
            active.current = false;
            setRunning(false);
            onLockedChange?.(!!pending.current);
        }
    };

    const reset = () => {
        pending.current = null;
        totals.current = emptyResult();
        setResult(null);
        setLastBatch(null);
        setDone(false);
        setRequestError("");
        setValidationError("");
        onLockedChange?.(false);
    };

    // A run is in progress until the server reports the last page or the user resets.
    const inProgress = result !== null && !done;
    const locked = running || inProgress;

    let statusKey: string;
    if (running) {
        statusKey = "processing";
    } else if (requestError) {
        statusKey = "cart_backfill_paused";
    } else if (!done) {
        statusKey = "cart_backfill_batch_completed";
    } else if (result && (result.totalErrors || result.errorDetails.length)) {
        statusKey = "cart_backfill_finished_with_errors";
    } else if (result && result.totalUnresolved) {
        statusKey = "cart_backfill_finished_with_unresolved";
    } else {
        statusKey = "cart_backfill_completed";
    }

    const renderTotals = (source: IBackfillCartCommissionsResult, testIdPrefix: string) => (
        <dl className="row">
            {totalKeys.map((key) => (
                <React.Fragment key={key}>
                    <dt className="col-8">
                        <OwTranslate id={totalLabels[key]} defaultMessage={totalLabels[key]} />
                    </dt>
                    <dd className="col-4" data-testid={`${testIdPrefix}${key}`}>
                        {source[key]}
                    </dd>
                </React.Fragment>
            ))}
        </dl>
    );

    return (
        <section className="mt-3 p-3 border rounded" aria-label={intl.formatMessage({ id: "backfill_cart_commissions" })}>
            <WhatsThis subject="help_text_cart_backfill_commissions" />
            <fieldset disabled={locked} className="border-0 p-0 m-0">
                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <OwLabel title="start_date" showIsReqAsterik />
                        <OwDateInput
                            portalId={portalId}
                            selectedDate={startDate}
                            disabled={locked}
                            fn={(date) => {
                                setStartDate(date);
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-6">
                        <OwLabel for={batchSizeId} title="batch_size" />
                        <OwFnInput
                            id={batchSizeId}
                            name="batchSize"
                            type="number"
                            min={1}
                            max={MAX_BATCH_SIZE}
                            value={batchSize}
                            disabled={locked}
                            fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setBatchSize(e.target.value);
                            }}
                        />
                    </div>
                </div>
            </fieldset>
            {validationError && (
                <div role="alert" className="alert alert-danger mt-2">
                    <OwTranslate id={validationError} defaultMessage={validationError} />
                </div>
            )}
            {requestError && (
                <div role="alert" className="alert alert-danger mt-2">
                    {requestError} <OwTranslate id="cart_backfill_request_failed_hint" defaultMessage="cart_backfill_request_failed_hint" />
                </div>
            )}
            <div className="d-flex flex-wrap gap-2 mt-3">
                {/* ariaLabel keeps the accessible name while the active spinner replaces the label. */}
                <OwButton
                    title="run"
                    ariaLabel={intl.formatMessage({ id: "run" })}
                    fn={() => void process("run")}
                    disabled={locked}
                    active={running}
                />
                {result && (
                    <OwButton
                        title="continue"
                        fn={() => void process("continue")}
                        disabled={running || done || !!requestError || result.nextAfterId === null}
                    />
                )}
                {requestError && <OwButton title="retry_failed_batch" fn={() => void process("retry")} disabled={running} />}
                {result && <OwButton title="reset" className="btn-secondary" disabled={running} fn={reset} />}
            </div>
            {result && (
                <div className="mt-3" aria-live="polite">
                    <p data-testid="backfillStatus">
                        <OwTranslate id={statusKey} defaultMessage={statusKey} />
                    </p>
                    <p>
                        <OwTranslate id="next_after_id" defaultMessage="next_after_id" />:{" "}
                        <span data-testid="nextAfterId">
                            {done ? (
                                <OwTranslate id="all_matching_orders_processed" defaultMessage="all_matching_orders_processed" />
                            ) : (
                                (result.nextAfterId ?? "-")
                            )}
                        </span>
                    </p>
                    <h6>
                        <OwTranslate id="cumulative_totals" defaultMessage="cumulative_totals" />
                    </h6>
                    {renderTotals(result, "")}
                    {lastBatch && (
                        <>
                            <h6>
                                <OwTranslate id="last_batch" defaultMessage="last_batch" />
                            </h6>
                            {renderTotals(lastBatch, "lastBatch.")}
                        </>
                    )}
                    {result.unresolvedDetails.length > 0 && (
                        <>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                <h6 className="mb-0">
                                    <OwTranslate id="unresolved_orders" defaultMessage="unresolved_orders" />
                                </h6>
                                <OwButton
                                    title="download_unresolved_csv"
                                    leftIconClass="fas fa-download"
                                    fn={() =>
                                        UIService.downloadCSVData(
                                            Papa.unparse(result.unresolvedDetails, {
                                                columns: ["orderGuid", "reason"],
                                                escapeFormulae: true,
                                            }),
                                            "cart-commission-backfill-unresolved.csv"
                                        )
                                    }
                                />
                            </div>
                            <ul>
                                {result.unresolvedDetails.map((detail, index) => (
                                    <li key={index}>
                                        {detail.orderGuid}: {detail.reason}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {result.errorDetails.length > 0 && (
                        <>
                            <h6>
                                <OwTranslate id="individual_errors" defaultMessage="individual_errors" />
                            </h6>
                            <ul>
                                {result.errorDetails.map((detail, index) => (
                                    <li key={index}>
                                        {detail.orderGuid}: {detail.error}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
